import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Question from "../../components/Question";
import { Box, FormControl, Chip, Stack, useTheme, useMediaQuery } from "@mui/material";
import { setPatientHistory } from "../../redux/userSlice";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

const OPTIONS = [
  "Liver disease",
  "Diabetes",
  "Previous pancreatitis",
  "History of elevated triglycerides",
  "History or current diagnosis of inflammatory bowel disease (ulcerative colitis or Crohn's disease)",
  "Previous isotretetinoin (Accutane) use",
  "History of depression",
  "Thoughts of suicide (past or present)",
  "Attempted suicide",
];

export default function PatientHistoryMedical() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const user = useSelector((state) => state.user);
  const [selected, setSelected] = useState([]);

  const labelToKey = useMemo(
    () => ({
      "Liver disease": "liverDisease",
      Diabetes: "diabetes",
      "Previous pancreatitis": "pancreatitis",
      "History of elevated triglycerides": "elevatedTriglycerides",
      "History or current diagnosis of inflammatory bowel disease (ulcerative colitis or Crohn's disease)":
        "inflammatoryBowelDisease",
      Smoke: "smoking",
      "Alcohol use": "alcohol",
      "Illicit drug use": "illicitDrugs",
      "Contact lens use": "contactLens",
      "Previous isotretetinoin (Accutane) use": "priorIsotretinoinUse",
      "History of depression": "historyOfDepression",
      "Thoughts of suicide (past or present)": "suicidalThoughts",
      "Attempted suicide": "suicideAttempt",
    }),
    []
  );

  const keyToLabel = useMemo(
    () => Object.fromEntries(Object.entries(labelToKey).map(([label, key]) => [key, label])),
    [labelToKey]
  );

  useEffect(() => {
    if (user.patientHistory) {
      const savedYes = Object.entries(user.patientHistory)
        .filter(([_, value]) => value === "Y")
        .map(([key]) => keyToLabel[key] || null)
        .filter(Boolean)
        .filter((label) => OPTIONS.includes(label));

      setSelected(savedYes);
    }
  }, [user.patientHistory, keyToLabel]);

  const toggle = (label) => {
    setSelected((prev) => (prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]));
  };

  const handleNext = () => {
    const prev = user.patientHistory || {};
    const updatesForThisScreen = Object.fromEntries(
      OPTIONS.map((label) => {
        const key = labelToKey[label];
        return [key, selected.includes(label) ? "Y" : "N"];
      })
    );

    dispatch(setPatientHistory({ ...prev, ...updatesForThisScreen }));
    setPageAndDeferNavigate(dispatch, "/patient-history-lifestyle", navigate);
  };

  const handleBack = () => {
    setPageAndDeferNavigate(dispatch, "/acne-treatment", navigate);
  };

  return (
    <Question
      onBack={handleBack}
      title="Please tell us about your health"
      subtitle="Please select any conditions you have or have had in the past."
      input={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl sx={{ width: "100%", mb: "1rem" }}>
            <Box sx={{ width: "100%", maxWidth: isDesktop ? "800px" : "100%", mx: "auto", mt: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {OPTIONS.map((label) => {
                  const isSelected = selected.includes(label);
                  return (
                    <Chip
                      key={label}
                      label={label}
                      clickable
                      onClick={() => toggle(label)}
                      variant={isSelected ? "filled" : "outlined"}
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: isSelected ? 600 : 400,
                        backgroundColor: isSelected ? "#82B1FF33" : "transparent",
                        color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                        borderColor: isSelected ? theme.palette.primary.main : theme.palette.grey[400],
                        height: "auto",
                        alignItems: "flex-start",
                        maxWidth: isDesktop ? "380px" : "100%",
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          lineHeight: 1.2,
                          paddingTop: "6px",
                          paddingBottom: "6px",
                        },
                        "&:hover": {
                          backgroundColor: isSelected ? "#82B1FF33" : theme.palette.action.hover,
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </FormControl>
        </Box>
      }
      footer={
        <Box sx={{ ...continueButtonSx }}>
          <ContinueButton onClick={handleNext} title="Continue" selected />
        </Box>
      }
    />
  );
}
